/**
 * Who gets the new influencer flow (modules/influencer).
 *
 * - An Influencer account: always (the creator signed in as themself).
 * - A Talent Partner: once they have picked one of their creators on
 *   /switch/user. switchuser.tsx then sets UserData to
 *   `{ ...owner, influencer_ids, ...creatorRow }`, so `influencer_id` is the
 *   picked creator's (auth_influencers) while account_id / user_id stay the
 *   Talent Partner's, and the brd API accepts the creator through
 *   lnk_account_influencers. Before a pick, UserData is the Talent Partner's own
 *   row (its 'Individual' influencer), which is not a creator to act for.
 *
 * `owner` is the signed-in account (state.SwitchLayer2Reducer.switchUser).
 */

const num = (v: unknown) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export const isTalentPartner = (user: any) => String(user?.account_type || '') === 'Talent Partner';

/** A Talent Partner acting for one of their creators. */
export const isTalentPartnerOnCreator = (user: any, owner: any) => {
  if (!isTalentPartner(user) || !Array.isArray(user?.influencer_ids)) return false;
  const creatorId = num(user?.influencer_id);
  return creatorId > 0 && creatorId !== num(owner?.influencer_id);
};

/** Whether the new influencer flow's pages, menus and links apply to this user. */
export const usesInfluencerFlow = (user: any, owner: any) =>
  String(user?.account_type || '') === 'Influencer' || isTalentPartnerOnCreator(user, owner);

/**
 * The creator's name for their own pages (Storefront, Link in Bio): a Talent
 * Partner's UserData keeps the Talent Partner's full_name / account_name, and
 * the picked creator's is `name` (auth_influencers).
 */
export const creatorDisplayName = (user: any): string =>
  // Never the Talent Partner's own name for a creator without one.
  String((isTalentPartner(user) ? user?.name : user?.full_name || user?.account_name) || '').trim();
